import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography } from '@mui/material';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';
import KnowledgeBaseActivity, { KnowledgeBaseRun, EvidenceSelection } from '../components/KnowledgeBaseActivity';
import SourceDocumentsDialog, { SourceDocument } from '../components/SourceDocumentsDialog';
import { useAuth } from '../contexts/AuthContext';

type Source = SourceDocument & { corpus: string; id: string; title: string; publisher: string; page: number | string | null; url: string; excerpt: string };
type Reply = { workflow?: { engine: string; retrieval_rounds: number; neighbour_passages: number };  knowledge_bases?: KnowledgeBaseRun[]; evidence_selection?: EvidenceSelection; title?: string | null; content: string; sources: Source[]; warnings: string[]; prompt_version: string; knowledge_indexes: string[] };
type Message = { role: 'user' | 'assistant'; content: string; timestamp?: string; reply?: Reply };
type PendingRequest = { id: string; question: string; status: string; created_at?: string };
type RequestStatus = { status: string; question: string; error?: string; error_code?: number; session?: Session };
type Session = { active_request?: PendingRequest | null; revision?: number; id: string; title: string; created_at?: string; updated_at?: string; messages: Message[] };

// randomUUID is restricted to secure contexts; getRandomValues also works on HTTP.
function createRequestId(): string {
  if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function KB2ChatPage() {
  const { currentUser, loading: authLoading } = useAuth();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selected, setSelected] = useState('');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [statusNotice, setStatusNotice] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const sending = useRef(false);
  const [sourceDocuments, setSourceDocuments] = useState<SourceDocument[] | null>(null);
  const generation = useRef(0);
  const session = sessions.find(s => s.id === selected);
  const pendingId = session?.active_request?.id;
  const pendingQuestion = session?.active_request?.question;
  const pendingStarted = session?.active_request?.created_at;
  const working = busy || !!pendingId;
  useEffect(() => {
    generation.current += 1;
    setSessions([]); setSelected(''); setQuestion(''); setError(''); setBusy(false);
    setEnabled(null); setSourceDocuments(null); setStatusNotice(''); sending.current = false;
    if (authLoading || !currentUser?.email) return;
    let active = true;
    setBusy(true);
    async function load() {
      try {
        const config = await api.get('/llm/chat-kb2/config');
        if (!active) return;
        setEnabled(config.data.enabled);
        if (!config.data.enabled) return;
        const list = await api.get<Session[]>('/llm/chat-kb2/sessions');
        if (!active) return;
        // Empty drafts can be newer than the last completed conversation.
        // Recover in-flight work first, then saved history; all drafts stay listed.
        const latest = list.data.find(s => s.active_request) || list.data.find(s => (s.revision ?? 0) > 0) || list.data[0];
        const restored = latest ? await api.get<Session>(`/llm/chat-kb2/sessions/${latest.id}`) : await api.post<Session>('/llm/chat-kb2/sessions');
        if (!active) return;
        setSessions([restored.data, ...list.data.filter(s => s.id !== restored.data.id).map(s => ({ ...s, messages: [] }))]);
        setSelected(restored.data.id);
      } catch { if (active) setError('Unable to load saved conversations. Refresh to retry.'); }
      finally { if (active) setBusy(false); }
    }
    void load();
    return () => { active = false; generation.current += 1; };
  }, [currentUser?.email, authLoading]);

  const conversationTitle = session?.title;
  const conversationRevision = session?.revision;
  useEffect(() => {
    if (!selected || conversationTitle !== 'Untitled conversation' || !conversationRevision) return;
    let active = true;
    let pending = false;
    const token = generation.current;
    const timer = window.setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const { data } = await api.get<Session[]>('/llm/chat-kb2/sessions');
        if (!active || token !== generation.current) return;
        setSessions(prev => prev.map(item => {
          const saved = data.find(s => s.id === item.id);
          return saved ? { ...item, title: saved.title } : item;
        }));
      } catch { /* Naming is optional; keep the saved conversation usable. */ }
      finally { pending = false; }
    }, 3000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 120000);
    return () => { active = false; window.clearInterval(timer); window.clearTimeout(stop); };
  }, [selected, conversationTitle, conversationRevision]);

  useEffect(() => {
    if (!selected || !pendingId) return;
    let active = true;
    let inProgress = false;
    let missing = 0;
    const token = generation.current;
    const started = pendingStarted ? Date.parse(pendingStarted) : Date.now();
    setQuestion(current => current || pendingQuestion || '');
    async function poll() {
      if (inProgress) return;
      inProgress = true;
      try {
        const { data } = await api.get<RequestStatus>(`/llm/chat-kb2/sessions/${selected}/requests/${pendingId}`, { timeout: 15000 });
        if (!active || token !== generation.current) return;
        missing = 0;
        if (data.status === 'processing') { setStatusNotice(''); return; }
        if (data.session) setSessions(prev => prev.map(s => s.id === selected ? data.session! : s));
        else setSessions(prev => prev.map(s => s.id === selected ? { ...s, active_request: null } : s));
        setStatusNotice('');
        if (data.status === 'completed') {
          setQuestion(current => current.trim() === data.question ? '' : current);
          setError('');
        } else {
          setQuestion(current => current || data.question);
          setError(data.error || 'This question could not be completed. Please retry.');
        }
      } catch (e: any) {
        if (!active || token !== generation.current) return;
        if ([401, 403].includes(e.response?.status)) {
          setSessions(prev => prev.map(s => s.id === selected ? { ...s, active_request: null } : s));
          setError('Your session is unavailable. Sign in again to recover the saved conversation.');
        } else if (e.response?.status === 404 && ++missing >= 10) {
          setSessions(prev => prev.map(s => s.id === selected ? { ...s, active_request: null } : s));
          setError('The server could not confirm this request. Your question has been kept; refresh before retrying.');
        } else if (e.response?.status !== 404) {
          setStatusNotice('Connection interrupted. Checking for your saved answer; please do not resubmit.');
        }
      } finally { inProgress = false; }
    }
    void poll();
    const timer = window.setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)));
      void poll();
    }, 2000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selected, pendingId, pendingQuestion, pendingStarted]);

  async function selectChat(id: string) {
    const token = generation.current;
    setBusy(true); setError('');
    try {
      const { data } = await api.get<Session>(`/llm/chat-kb2/sessions/${id}`);
      if (token !== generation.current) return;
      setSessions(prev => prev.map(s => s.id === id ? data : s));
      setSelected(id); setQuestion('');
    } catch { if (token === generation.current) setError('Unable to load this conversation.'); }
    finally { if (token === generation.current) setBusy(false); }
  }

  async function startNewChat() {
    const draft = sessions.find(s => s.revision === 0);
    if (draft) { await selectChat(draft.id); return; }
    const token = generation.current;
    setBusy(true); setError('');
    try {
      const { data } = await api.post<Session>('/llm/chat-kb2/sessions');
      if (token !== generation.current) return;
      setSessions(prev => [data, ...prev]); setSelected(data.id); setQuestion('');
    } catch { if (token === generation.current) setError('Unable to create a conversation.'); }
    finally { if (token === generation.current) setBusy(false); }
  }

  async function deleteChat() {
    const token = generation.current;
    setBusy(true); setError('');
    try {
      await api.delete(`/llm/chat-kb2/sessions/${selected}`);
      const remaining = sessions.filter(s => s.id !== selected);
      const { data } = remaining.length ? await api.get<Session>(`/llm/chat-kb2/sessions/${remaining[0].id}`) : await api.post<Session>('/llm/chat-kb2/sessions');
      if (token !== generation.current) return;
      setSessions([data, ...remaining.filter(s => s.id !== data.id)]); setSelected(data.id); setQuestion('');
    } catch { if (token === generation.current) setError('Unable to delete or refresh the conversation. Refresh to retry.'); }
    finally { if (token === generation.current) setBusy(false); }
  }

  async function send() {
    if (!question.trim() || working || !session || sending.current) return;
    sending.current = true;
    const id = session.id;
    const token = generation.current;
    const text = question.trim();
    let requestId: string;
    try { requestId = createRequestId(); }
    catch { sending.current = false; setError('Unable to start a request. Open Chat in an up-to-date browser over HTTPS.'); return; }
    const pending: PendingRequest = { id: requestId, question: text, status: 'processing', created_at: new Date().toISOString() };
    setBusy(true); setError(''); setStatusNotice(''); setElapsed(0);
    setSessions(prev => prev.map(s => s.id === id ? { ...s, active_request: pending } : s));
    try {
      await api.post(`/llm/chat-kb2/sessions/${id}/messages`, { question: text, request_id: requestId, revision: session.revision }, { timeout: 15000 });
    } catch (e: any) {
      if (token !== generation.current) return;
      if (e.response?.status && e.response.status < 500) {
        try {
          const { data } = await api.get<Session>(`/llm/chat-kb2/sessions/${id}`);
          if (token !== generation.current) return;
          setSessions(prev => prev.map(s => s.id === id ? data : s));
          setError(e.response.status === 409 ? 'The conversation was refreshed. A previous question may still be processing; review its result before retrying.' : typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Unable to submit your question.');
        } catch {
          if (token === generation.current) setStatusNotice('Checking whether your question was accepted. Please do not resubmit.');
        }
      } else {
        setStatusNotice('Checking for your saved answer. Please do not resubmit.');
      }
    } finally {
      if (token === generation.current) { setBusy(false); sending.current = false; }
    }
  }

  return <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
    <Box component="header" sx={{ mb: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1.5 }}>Chat</Typography>
      <Typography color="text.secondary" sx={{ maxWidth: '85ch', lineHeight: 1.7, mb: 1 }}>
        Explore climate risk and reporting requirements through our chat feature. Responses draw on curated public sources chosen for quality and relevance to Australian climate risk reporting (not the wild web).
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: '85ch', lineHeight: 1.7, mb: 1.5 }}>
        We’ll help you prepare for reporting and identify some actions and opportunities to work towards. You can see how each answer was generated and where to go for more information.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        Your conversations are saved to your account.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 0.5 }}>
        <Box component="strong">Reminder: This is a prototype and intended for demonstration only. AI can make mistakes.</Box>
      </Typography>
    </Box>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {enabled === null ? <CircularProgress /> : !enabled ? <Alert severity="info">Knowledge Base 2 chat is not enabled in this environment.</Alert> : <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button disabled={working} variant="outlined" onClick={startNewChat}>New chat</Button>
        <TextField id="kb2-conversation" select InputLabelProps={{ htmlFor: undefined }} label="Conversation" size="small" value={selected} disabled={working}
          sx={{ minWidth: 220, maxWidth: '100%', flex: 1 }}
          onChange={e => { void selectChat(e.target.value); }}>
          {sessions.map(s => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
        </TextField>
        <Button disabled={working || !session} onClick={deleteChat}>Delete chat</Button>
      </Box>
      {!session?.messages.length && !pendingId && <Typography sx={{ my: 3 }}>Ask about disclosure requirements, climate risks, or practical responses for your business.</Typography>}
      {session?.messages.map((m, i) => <Paper key={i} sx={{ p: 2, my: 2 }}>
        <Typography fontWeight={600}>{m.role === 'user' ? 'You' : 'Chat'}</Typography>
        {m.role === 'assistant' ? <Box sx={{ overflowWrap: 'anywhere', '& a': { color: 'primary.main' }, '& pre': { overflowX: 'auto' } }}><ReactMarkdown>{m.content}</ReactMarkdown></Box> : <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</Typography>}
        {m.reply?.knowledge_bases && <KnowledgeBaseActivity bases={m.reply.knowledge_bases} selection={m.reply.evidence_selection} onViewSources={corpus => setSourceDocuments(m.reply!.sources.filter(source => source.corpus === corpus))} />}
        {m.reply?.workflow && <Typography variant="caption" color="text.secondary">Evidence checked across {m.reply.workflow.retrieval_rounds} search round{m.reply.workflow.retrieval_rounds === 1 ? '' : 's'}, including {m.reply.workflow.neighbour_passages} neighbouring passage{m.reply.workflow.neighbour_passages === 1 ? '' : 's'}.</Typography>}
        {m.reply?.warnings.map(w => <Alert severity="warning" key={w} sx={{ mt: 1 }}>{w}</Alert>)}
        {m.reply?.evidence_selection?.status === 'completed' && !!m.reply.evidence_selection.gaps.length && <Box component="details" sx={{ mt: 2, color: 'text.secondary' }}>
          <summary>Evidence limitations</summary>
          <Typography variant="body2" sx={{ mt: 1 }}>The evidence assessment identified these possible gaps in the selected passages. They do not mean the entire knowledge base lacks this information.</Typography>
          <Box component="ul" sx={{ pl: 3 }}>{m.reply.evidence_selection.gaps.map((gap, index) => <Typography key={index} component="li" variant="body2">{gap}</Typography>)}</Box>
        </Box>}
      </Paper>)}
      {pendingId && <Paper sx={{ p: 2, my: 2 }}>
        <Typography fontWeight={600}>You</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{pendingQuestion}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Processing your question… {elapsed > 0 ? `${elapsed}s elapsed.` : ''} Your answer will appear here when ready.</Typography>
      </Paper>}
      {statusNotice && <Alert severity="info" sx={{ my: 1 }}>{statusNotice}</Alert>}
      {working && <KnowledgeBaseActivity pending />}
      <Box component="form" onSubmit={e => { e.preventDefault(); void send(); }} sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField id="kb2-question" fullWidth multiline minRows={2} label="Your question" value={question} disabled={working} inputProps={{ maxLength: 12000 }} onChange={e => setQuestion(e.target.value)} />
        <Button type="submit" variant="contained" disabled={working || !session || !question.trim()}>{working ? <CircularProgress size={24} /> : 'Send'}</Button>
      </Box>
    </>}
    <SourceDocumentsDialog open={sourceDocuments !== null} documents={sourceDocuments || []} onClose={() => setSourceDocuments(null)} />
  </Box>;
}
